<script setup>
import { ref, computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'


const props = defineProps({
  estimation:                { type: Object,  default: () => ({}) },
  projet:                    { type: Object,  default: () => ({}) },
  rapport:                   { type: Object,  default: () => ({}) },
  element_groups:            { type: Array,   default: () => [] },
  estimation_partie_suivies: { type: Array,   default: () => [] },
  personnel_additionnels:    { type: Array,   default: () => [] },
  equipement_additionnels:   { type: Array,   default: () => [] },
  inventaire_additionnels:   { type: Array,   default: () => [] },
  type_liaison_simples:      { type: Array,   default: () => [] },
  meteo_options:             { type: Array,   default: () => [] },
  show_meteo:                { type: Boolean, default: false },
  show_camionneurs:          { type: Boolean, default: false },
  show_prenom:               { type: Boolean, default: false },
  show_type_equipement:      { type: Boolean, default: false },
  show_type_inventaire:      { type: Boolean, default: false },
  inventaire_enabled:        { type: Boolean, default: false },
  liaison_enabled:           { type: Boolean, default: false },
  has_partie_suivies:        { type: Boolean, default: false },
  has_personnel_externes:    { type: Boolean, default: false },
  has_equipement_externes:   { type: Boolean, default: false },
  errors:                    { type: Object,  default: () => ({}) },
  rapports_url:              { type: String,  default: '/rapports' },
  create_url:                { type: String,  default: '/rapports' },
})

// ── Classes utilitaires ──────────────────────────────────────
const inputCls   = 'w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all'
const inputSmCls = 'bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-sm text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all'
const sectionHdrCls   = 'px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between'
const sectionTitleCls = 'text-[11px] font-black uppercase tracking-widest text-outline'
const labelCls   = 'block text-[10px] font-black uppercase tracking-widest text-outline mb-1.5'
const addBtnCls  = 'flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all'
const thCls      = 'px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline'
const tdCls      = 'px-4 py-3'

// ── Items groupés — copie réactive ───────────────────────────
const formGroups = ref(
  props.element_groups.map(g => ({
    tag: g.tag,
    elements: g.elements.map(e => ({ ...e })),
  }))
)

// ── Liaison simples (HABTM checkboxes) ───────────────────────
const selectedLiaisonSimpleIds = ref([])

// ── Nouvelles images ─────────────────────────────────────────
const newImageFiles    = ref([])
const newImagePreviews = ref([])

function onImageChange(e) {
  Array.from(e.target.files || []).forEach(file => {
    newImageFiles.value.push(file)
    newImagePreviews.value.push({ name: file.name, src: URL.createObjectURL(file) })
  })
  e.target.value = ''
}

function removeNewImage(index) {
  newImageFiles.value.splice(index, 1)
  newImagePreviews.value.splice(index, 1)
}

// ── Formulaire Inertia ────────────────────────────────────────
const form = useForm({
  estimation_id: props.rapport.estimation_id,
  date:          props.rapport.date,
  commentaires:  props.rapport.commentaires,
  meteo:         props.rapport.meteo,

  rapport_personnels_attributes:
    (props.rapport.rapport_personnels_attributes ?? []).map(r => ({ ...r })),
  rapport_equipements_attributes:
    (props.rapport.rapport_equipements_attributes ?? []).map(r => ({ ...r })),
  rapport_inventaires_attributes:
    (props.rapport.rapport_inventaires_attributes ?? []).map(r => ({ ...r })),
  rapport_personnel_additionnels_attributes:            [],
  rapport_equipement_additionnels_attributes:           [],
  rapport_inventaire_additionnels_attributes:           [],
  rapport_partie_suivie_elements_attributes:
    (props.rapport.rapport_partie_suivie_elements_attributes ?? []).map(r => ({ ...r })),
  rapport_partie_suivie_element_additionnels_attributes: [],
  rapport_personnel_externes_attributes:
    (props.rapport.rapport_personnel_externes_attributes ?? []).map(r => ({ ...r })),
  rapport_equipement_externes_attributes:
    (props.rapport.rapport_equipement_externes_attributes ?? []).map(r => ({ ...r })),
  rapport_chainages_attributes:
    (props.rapport.rapport_chainages_attributes ?? []).map(r => ({ ...r })),
  liaisons_attributes:
    (props.rapport.liaisons_attributes ?? []).map(r => ({ ...r })),
  liaison_liste_textes_attributes:
    (props.rapport.liaison_liste_textes_attributes ?? []).map(r => ({ ...r, selected_ids: [] })),
  rapport_elements_attributes: [],
  type_liaison_simple_ids:     [],
  images:                      [],
})

// ── Erreurs ──────────────────────────────────────────────────
const allErrors = computed(() => ({ ...form.errors, ...props.errors }))
function err(f) { return allErrors.value[f] }

const { error: toastError } = useToast()
watch(allErrors, (errs) => {
  const msgs = Object.entries(errs).map(([f, m]) => `${f} : ${Array.isArray(m) ? m.join(', ') : m}`)
  if (msgs.length) toastError(msgs.join('\n'))
}, { deep: true })

// ── Soumission ────────────────────────────────────────────────
function submit() {
  form.rapport_elements_attributes = formGroups.value.flatMap(g =>
    g.elements.map(e => ({ estimation_element_id: e.estimation_element_id, quantite: e.quantite }))
  )
  form.type_liaison_simple_ids = [...selectedLiaisonSimpleIds.value]
  form.images = [...newImageFiles.value]
  form.transform(data => ({ rapport: data }))
      .post(props.create_url, { forceFormData: true })
}

// ── Additionnels ─────────────────────────────────────────────
const emptyPersonnelAdd  = () => ({ personnel_id: '', quantite_regulier: '', quantite_supplementaire: '', am: false, pm: false, inspection: false })
const emptyEquipementAdd = () => ({ equipement_id: '', quantite: '' })
const emptyInventaireAdd = () => ({ inventaire_id: '', quantite: '' })
const emptyPartieAdd     = () => ({ estimation_partie_suivie_id: '', nom: '', quantite: '', taux_unitaire: '' })

function addRow(list, template) { list.push({ ...template }) }
function removeRow(list, i)     { list.splice(i, 1) }

// ── Météo labels ─────────────────────────────────────────────
const meteoLabels = {
  'wi-day-sunny':'Ensoleillé','wi-cloudy':'Nuageux','wi-cloudy-gusts':'Rafales',
  'wi-cloudy-windy':'Venteux/nuageux','wi-fog':'Brouillard','wi-sprinkle':'Bruine',
  'wi-showers':'Averses','wi-rain':'Pluie','wi-rain-wind':'Pluie et vent',
  'wi-storm-showers':'Orages/averses','wi-thunderstorm':'Orage','wi-rain-mix':'Pluie mixte',
  'wi-sleet':'Grésil','wi-snow':'Neige','wi-snow-wind':'Neige et vent','wi-hail':'Grêle',
  'wi-windy':'Venteux','wi-strong-wind':'Grand vent','wi-hot':'Chaud',
  'wi-snowflake-cold':'Froid','wi-raindrop':'Goutte de pluie',
}

</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="estimation.url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <a :href="projet.url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-colors">{{ projet.code }}</a>
        <span class="text-outline">/</span>
        <a :href="estimation.url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-colors">{{ estimation.code }}</a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Nouveau bordereau</span>
      </div>
    </template>

    <div class="p-8">
      <div class="space-y-6">

        <!-- En-tête -->
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">Nouveau bordereau</h1>
          <p class="text-sm text-outline mt-1">
            <a :href="projet.url" class="font-mono font-bold text-primary hover:brightness-125">{{ projet.code }}</a>
            &rsaquo;
            <a :href="estimation.url" class="font-mono font-bold text-primary hover:brightness-125">{{ estimation.code }}</a>
            <span class="ml-2 text-outline/60">{{ estimation.rapports_count }} rapport(s) existant(s)</span>
          </p>
        </div>

        <form @submit.prevent="submit" class="space-y-6">
          <input type="hidden" name="rapport[estimation_id]" :value="form.estimation_id" />

          <!-- ══════════════════════════════════════════
               1. INFORMATIONS
               ══════════════════════════════════════════ -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Informations</h2>
            </div>
            <div class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">

              <!-- Date -->
              <div>
                <label :class="labelCls">Date <span class="text-error">*</span></label>
                <input v-model="form.date" type="date" :class="[inputCls, err('date') ? 'ring-2 ring-error/40' : '']" />
                <p v-if="err('date')" class="text-xs text-error mt-1">{{ err('date') }}</p>
              </div>

              <!-- Météo -->
              <div v-if="show_meteo">
                <label :class="labelCls">Météo</label>
                <select v-model="form.meteo" :class="inputCls">
                  <option value="">—</option>
                  <option v-for="opt in meteo_options" :key="opt.value" :value="opt.value">
                    {{ meteoLabels[opt.key] ?? opt.key }}
                  </option>
                </select>
              </div>

              <!-- Chainages -->
              <div v-if="form.rapport_chainages_attributes.length" class="sm:col-span-2 space-y-3">
                <label :class="labelCls">Chaînage</label>
                <div v-for="(rc, i) in form.rapport_chainages_attributes" :key="i"
                     class="flex items-center gap-4">
                  <input type="hidden" :value="rc.type_chainage_id" :name="`rapport[rapport_chainages_attributes][${i}][type_chainage_id]`" />
                  <span class="text-sm text-on-surface/70 w-36 shrink-0">{{ rc.description }}</span>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-outline">De</span>
                    <input v-model="rc.champ1" type="text" :class="inputSmCls + ' w-24'" />
                    <template v-if="rc.champs > 1">
                      <span class="text-xs text-outline">à</span>
                      <input v-model="rc.champ2" type="text" :class="inputSmCls + ' w-24'" />
                    </template>
                    <span class="text-xs text-outline">{{ rc.unite }}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- ══════════════════════════════════════════
               2. PERSONNEL
               ══════════════════════════════════════════ -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Personnel</h2>
            </div>

            <!-- Personnel interne -->
            <div v-if="form.rapport_personnels_attributes.length" class="overflow-x-auto border-b border-outline-variant/10">
              <div class="px-6 pt-4 pb-1">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">Bordereau</p>
              </div>
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th :class="thCls">Code</th>
                    <th :class="thCls">Nom</th>
                    <th v-if="show_prenom" :class="thCls">Prénom</th>
                    <template v-if="show_camionneurs">
                      <th :class="thCls + ' text-center'">AM</th>
                      <th :class="thCls + ' text-center'">PM</th>
                      <th :class="thCls + ' text-center'">Inspection</th>
                    </template>
                    <th :class="thCls">Hrs rég.</th>
                    <th :class="thCls">Hrs supp.</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <tr v-for="(rp, i) in form.rapport_personnels_attributes" :key="i"
                      class="hover:bg-surface-container-highest/20 transition-colors">
                    <input type="hidden" :name="`rapport[rapport_personnels_attributes][${i}][estimation_personnel_id]`" :value="rp.estimation_personnel_id" />
                    <td :class="tdCls + ' font-mono font-bold text-primary'">{{ rp.code }}</td>
                    <td :class="tdCls + ' text-on-surface'">{{ rp.nom }}</td>
                    <td v-if="show_prenom" :class="tdCls + ' text-on-surface'">{{ rp.prenom }}</td>
                    <template v-if="show_camionneurs">
                      <td :class="tdCls + ' text-center'"><input v-model="rp.am" type="checkbox" class="rounded border-outline-variant/30 text-primary" /></td>
                      <td :class="tdCls + ' text-center'"><input v-model="rp.pm" type="checkbox" class="rounded border-outline-variant/30 text-primary" /></td>
                      <td :class="tdCls + ' text-center'"><input v-model="rp.inspection" type="checkbox" class="rounded border-outline-variant/30 text-primary" /></td>
                    </template>
                    <td :class="tdCls"><input v-model="rp.quantite_regulier" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                    <td :class="tdCls"><input v-model="rp.quantite_supplementaire" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Personnel additionnel -->
            <div class="border-b border-outline-variant/10">
              <div class="px-6 py-3 flex items-center justify-between">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">
                  Additionnel <span class="text-outline/30">({{ form.rapport_personnel_additionnels_attributes.length }})</span>
                </p>
                <button type="button" @click="addRow(form.rapport_personnel_additionnels_attributes, emptyPersonnelAdd())" :class="addBtnCls">
                  <span class="material-symbols-outlined text-sm">add</span>Ajouter
                </button>
              </div>
              <div v-if="!form.rapport_personnel_additionnels_attributes.length"
                   class="px-6 pb-4 text-sm text-outline/40 italic">Aucun — cliquez sur Ajouter</div>
              <div v-else class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls">Personnel</th>
                      <template v-if="show_camionneurs">
                        <th :class="thCls + ' text-center'">AM</th>
                        <th :class="thCls + ' text-center'">PM</th>
                        <th :class="thCls + ' text-center'">Insp.</th>
                      </template>
                      <th :class="thCls">Hrs rég.</th>
                      <th :class="thCls">Hrs supp.</th>
                      <th :class="thCls"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="(rpa, i) in form.rapport_personnel_additionnels_attributes" :key="i"
                        class="hover:bg-surface-container-highest/10">
                      <td :class="tdCls">
                        <select v-model="rpa.personnel_id" :class="inputSmCls + ' min-w-48'">
                          <option value="">—</option>
                          <option v-for="p in personnel_additionnels" :key="p.id" :value="p.id">{{ p.label }}</option>
                        </select>
                      </td>
                      <template v-if="show_camionneurs">
                        <td :class="tdCls + ' text-center'"><input v-model="rpa.am" type="checkbox" class="rounded" /></td>
                        <td :class="tdCls + ' text-center'"><input v-model="rpa.pm" type="checkbox" class="rounded" /></td>
                        <td :class="tdCls + ' text-center'"><input v-model="rpa.inspection" type="checkbox" class="rounded" /></td>
                      </template>
                      <td :class="tdCls"><input v-model="rpa.quantite_regulier" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                      <td :class="tdCls"><input v-model="rpa.quantite_supplementaire" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                      <td :class="tdCls">
                        <button type="button" @click="removeRow(form.rapport_personnel_additionnels_attributes, i)"
                                class="text-outline hover:text-error transition-colors">
                          <span class="material-symbols-outlined text-base">remove_circle</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Personnel externe -->
            <div v-if="has_personnel_externes && form.rapport_personnel_externes_attributes.length">
              <div class="px-6 pt-4 pb-1">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">Externe</p>
              </div>
              <div class="overflow-x-auto pb-4">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls">Code</th>
                      <th :class="thCls">Nom</th>
                      <th v-if="show_prenom" :class="thCls">Prénom</th>
                      <th :class="thCls">Hrs rég.</th>
                      <th :class="thCls">Hrs supp.</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="(rpe, i) in form.rapport_personnel_externes_attributes" :key="i"
                        class="hover:bg-surface-container-highest/10">
                      <input type="hidden" :name="`rapport[rapport_personnel_externes_attributes][${i}][personnel_externe_id]`" :value="rpe.personnel_externe_id" />
                      <td :class="tdCls + ' font-mono font-bold text-primary'">{{ rpe.code }}</td>
                      <td :class="tdCls + ' text-on-surface'">{{ rpe.nom }}</td>
                      <td v-if="show_prenom" :class="tdCls + ' text-on-surface'">{{ rpe.prenom }}</td>
                      <td :class="tdCls"><input v-model="rpe.quantite_regulier" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                      <td :class="tdCls"><input v-model="rpe.quantite_supplementaire" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               3. ÉQUIPEMENTS
               ══════════════════════════════════════════ -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Équipements</h2>
            </div>

            <!-- Internes -->
            <div v-if="form.rapport_equipements_attributes.length" class="overflow-x-auto border-b border-outline-variant/10">
              <div class="px-6 pt-4 pb-1">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">Bordereau</p>
              </div>
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th :class="thCls">Code</th>
                    <th v-if="show_type_equipement" :class="thCls">Type</th>
                    <th :class="thCls">Nom</th>
                    <th :class="thCls">Quantité</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <tr v-for="(re, i) in form.rapport_equipements_attributes" :key="i"
                      class="hover:bg-surface-container-highest/20 transition-colors">
                    <input type="hidden" :name="`rapport[rapport_equipements_attributes][${i}][estimation_equipement_id]`" :value="re.estimation_equipement_id" />
                    <td :class="tdCls + ' font-mono font-bold text-primary'">{{ re.code }}</td>
                    <td v-if="show_type_equipement" :class="tdCls + ' text-on-surface'">{{ re.type_equipement }}</td>
                    <td :class="tdCls + ' text-on-surface'">{{ re.nom }}</td>
                    <td :class="tdCls"><input v-model="re.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-24'" /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Additionnels -->
            <div class="border-b border-outline-variant/10">
              <div class="px-6 py-3 flex items-center justify-between">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">
                  Additionnel <span class="text-outline/30">({{ form.rapport_equipement_additionnels_attributes.length }})</span>
                </p>
                <button type="button" @click="addRow(form.rapport_equipement_additionnels_attributes, emptyEquipementAdd())" :class="addBtnCls">
                  <span class="material-symbols-outlined text-sm">add</span>Ajouter
                </button>
              </div>
              <div v-if="!form.rapport_equipement_additionnels_attributes.length"
                   class="px-6 pb-4 text-sm text-outline/40 italic">Aucun — cliquez sur Ajouter</div>
              <div v-else class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls">Équipement</th>
                      <th :class="thCls">Quantité</th>
                      <th :class="thCls"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="(rea, i) in form.rapport_equipement_additionnels_attributes" :key="i"
                        class="hover:bg-surface-container-highest/10">
                      <td :class="tdCls">
                        <select v-model="rea.equipement_id" :class="inputSmCls + ' min-w-48'">
                          <option value="">—</option>
                          <option v-for="e in equipement_additionnels" :key="e.id" :value="e.id">{{ e.label }}</option>
                        </select>
                      </td>
                      <td :class="tdCls"><input v-model="rea.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-24'" /></td>
                      <td :class="tdCls">
                        <button type="button" @click="removeRow(form.rapport_equipement_additionnels_attributes, i)"
                                class="text-outline hover:text-error transition-colors">
                          <span class="material-symbols-outlined text-base">remove_circle</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Externes -->
            <div v-if="has_equipement_externes && form.rapport_equipement_externes_attributes.length">
              <div class="px-6 pt-4 pb-1">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">Externe</p>
              </div>
              <div class="overflow-x-auto pb-4">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls">Code</th>
                      <th v-if="show_type_equipement" :class="thCls">Type</th>
                      <th :class="thCls">Nom</th>
                      <th :class="thCls">Quantité</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="(ree, i) in form.rapport_equipement_externes_attributes" :key="i"
                        class="hover:bg-surface-container-highest/10">
                      <input type="hidden" :name="`rapport[rapport_equipement_externes_attributes][${i}][equipement_externe_id]`" :value="ree.equipement_externe_id" />
                      <td :class="tdCls + ' font-mono font-bold text-primary'">{{ ree.code }}</td>
                      <td v-if="show_type_equipement" :class="tdCls + ' text-on-surface'">{{ ree.type_equipement }}</td>
                      <td :class="tdCls + ' text-on-surface'">{{ ree.nom }}</td>
                      <td :class="tdCls"><input v-model="ree.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-24'" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               4. INVENTAIRES
               ══════════════════════════════════════════ -->
          <div v-if="inventaire_enabled"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Inventaires</h2>
            </div>

            <!-- Internes -->
            <div v-if="form.rapport_inventaires_attributes.length" class="overflow-x-auto border-b border-outline-variant/10">
              <div class="px-6 pt-4 pb-1">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">Bordereau</p>
              </div>
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th :class="thCls">Code</th>
                    <th v-if="show_type_inventaire" :class="thCls">Type</th>
                    <th :class="thCls">Nom</th>
                    <th :class="thCls">Quantité</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <tr v-for="(ri, i) in form.rapport_inventaires_attributes" :key="i"
                      class="hover:bg-surface-container-highest/20 transition-colors">
                    <input type="hidden" :name="`rapport[rapport_inventaires_attributes][${i}][estimation_inventaire_id]`" :value="ri.estimation_inventaire_id" />
                    <td :class="tdCls + ' font-mono font-bold text-primary'">{{ ri.code }}</td>
                    <td v-if="show_type_inventaire" :class="tdCls + ' text-on-surface'">{{ ri.type_inventaire }}</td>
                    <td :class="tdCls + ' text-on-surface'">{{ ri.nom }}</td>
                    <td :class="tdCls"><input v-model="ri.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-24'" /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Additionnels -->
            <div>
              <div class="px-6 py-3 flex items-center justify-between">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">
                  Additionnel <span class="text-outline/30">({{ form.rapport_inventaire_additionnels_attributes.length }})</span>
                </p>
                <button type="button" @click="addRow(form.rapport_inventaire_additionnels_attributes, emptyInventaireAdd())" :class="addBtnCls">
                  <span class="material-symbols-outlined text-sm">add</span>Ajouter
                </button>
              </div>
              <div v-if="!form.rapport_inventaire_additionnels_attributes.length"
                   class="px-6 pb-4 text-sm text-outline/40 italic">Aucun — cliquez sur Ajouter</div>
              <div v-else class="overflow-x-auto pb-4">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls">Inventaire</th>
                      <th :class="thCls">Quantité</th>
                      <th :class="thCls"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="(ria, i) in form.rapport_inventaire_additionnels_attributes" :key="i"
                        class="hover:bg-surface-container-highest/10">
                      <td :class="tdCls">
                        <select v-model="ria.inventaire_id" :class="inputSmCls + ' min-w-48'">
                          <option value="">—</option>
                          <option v-for="inv in inventaire_additionnels" :key="inv.id" :value="inv.id">{{ inv.label }}</option>
                        </select>
                      </td>
                      <td :class="tdCls"><input v-model="ria.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-24'" /></td>
                      <td :class="tdCls">
                        <button type="button" @click="removeRow(form.rapport_inventaire_additionnels_attributes, i)"
                                class="text-outline hover:text-error transition-colors">
                          <span class="material-symbols-outlined text-base">remove_circle</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               5. ITEMS
               ══════════════════════════════════════════ -->
          <div v-if="formGroups.some(g => g.elements.length)"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Items</h2>
            </div>
            <div class="divide-y divide-outline-variant/15">
              <div v-for="(group, gi) in formGroups" :key="gi">
                <div v-if="group.elements.length">
                  <div class="px-6 py-2 bg-surface-container-high/20">
                    <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">{{ group.tag ?? 'Sans catégorie' }}</p>
                  </div>
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm border-collapse">
                      <thead>
                        <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                          <th :class="thCls + ' w-24'">Code</th>
                          <th :class="thCls">Description</th>
                          <th :class="thCls + ' w-24'">Quantité</th>
                          <th :class="thCls + ' w-36'">Cumulé / Prévu</th>
                          <th :class="thCls + ' w-28'">Balance</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-outline-variant/10">
                        <tr v-for="(el, ei) in group.elements" :key="ei"
                            class="hover:bg-surface-container-highest/20 transition-colors">
                          <td :class="tdCls + ' font-mono font-bold text-primary'">{{ el.code }}</td>
                          <td :class="tdCls + ' text-on-surface'">{{ el.description }}</td>
                          <td :class="tdCls">
                            <input v-model="el.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" />
                          </td>
                          <td :class="tdCls + ' font-mono text-on-surface/70 text-xs'">
                            {{ el.quantite_somme }} / {{ el.quantite_prevue }} {{ el.type_unite }}
                          </td>
                          <td :class="tdCls">
                            <span :class="['font-mono text-xs font-bold',
                              Number(el.quantite_balance) < 0  ? 'text-error' :
                              Number(el.quantite_balance) === 0 ? 'text-outline' : 'text-success']">
                              {{ el.quantite_balance }} {{ el.type_unite }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               6. PARTIES SUIVIES
               ══════════════════════════════════════════ -->
          <div v-if="has_partie_suivies"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Parties suivies</h2>
            </div>

            <!-- Éléments internes -->
            <div v-if="form.rapport_partie_suivie_elements_attributes.length" class="overflow-x-auto border-b border-outline-variant/10">
              <div class="px-6 pt-4 pb-1">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">Bordereau</p>
              </div>
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th :class="thCls">Partie</th>
                    <th :class="thCls">Code</th>
                    <th :class="thCls">Description</th>
                    <th :class="thCls">Quantité</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <tr v-for="(rpse, i) in form.rapport_partie_suivie_elements_attributes" :key="i"
                      class="hover:bg-surface-container-highest/20 transition-colors">
                    <input type="hidden" :name="`rapport[rapport_partie_suivie_elements_attributes][${i}][estimation_partie_suivie_element_id]`" :value="rpse.estimation_partie_suivie_element_id" />
                    <td :class="tdCls + ' font-semibold text-outline'">{{ rpse.type_partie_code }}</td>
                    <td :class="tdCls + ' font-mono font-bold text-primary'">{{ rpse.code }}</td>
                    <td :class="tdCls + ' text-on-surface'">{{ rpse.description }}</td>
                    <td :class="tdCls">
                      <div class="flex items-center gap-2">
                        <input v-model="rpse.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" />
                        <span class="text-xs text-outline">{{ rpse.unite }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Additionnels -->
            <div>
              <div class="px-6 py-3 flex items-center justify-between">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline/50">
                  Additionnel <span class="text-outline/30">({{ form.rapport_partie_suivie_element_additionnels_attributes.length }})</span>
                </p>
                <button type="button" @click="addRow(form.rapport_partie_suivie_element_additionnels_attributes, emptyPartieAdd())" :class="addBtnCls">
                  <span class="material-symbols-outlined text-sm">add</span>Ajouter
                </button>
              </div>
              <div v-if="!form.rapport_partie_suivie_element_additionnels_attributes.length"
                   class="px-6 pb-4 text-sm text-outline/40 italic">Aucun — cliquez sur Ajouter</div>
              <div v-else class="overflow-x-auto pb-4">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/20 border-b border-outline-variant/10">
                      <th :class="thCls">Partie suivie</th>
                      <th :class="thCls">Nom</th>
                      <th :class="thCls">Quantité</th>
                      <th :class="thCls">Taux unitaire</th>
                      <th :class="thCls"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <tr v-for="(rpsea, i) in form.rapport_partie_suivie_element_additionnels_attributes" :key="i"
                        class="hover:bg-surface-container-highest/10">
                      <td :class="tdCls">
                        <select v-model="rpsea.estimation_partie_suivie_id" :class="inputSmCls + ' min-w-36'">
                          <option value="">—</option>
                          <option v-for="eps in estimation_partie_suivies" :key="eps.id" :value="eps.id">{{ eps.code }}</option>
                        </select>
                      </td>
                      <td :class="tdCls"><input v-model="rpsea.nom" type="text" :class="inputSmCls + ' w-32'" /></td>
                      <td :class="tdCls"><input v-model="rpsea.quantite" type="number" step="0.01" min="0" :class="inputSmCls + ' w-20'" /></td>
                      <td :class="tdCls"><input v-model="rpsea.taux_unitaire" type="number" step="0.01" min="0" :class="inputSmCls + ' w-24'" /></td>
                      <td :class="tdCls">
                        <button type="button" @click="removeRow(form.rapport_partie_suivie_element_additionnels_attributes, i)"
                                class="text-outline hover:text-error transition-colors">
                          <span class="material-symbols-outlined text-base">remove_circle</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               7. LIAISONS
               ══════════════════════════════════════════ -->
          <div v-if="liaison_enabled"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Liaisons</h2>
            </div>
            <div class="p-6 space-y-6">

              <!-- Liaisons simples (HABTM) -->
              <div v-if="type_liaison_simples.length">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <label v-for="tls in type_liaison_simples" :key="tls.id"
                         class="flex items-center gap-2 text-sm text-on-surface cursor-pointer group">
                    <input type="checkbox" :value="tls.id" v-model="selectedLiaisonSimpleIds"
                           class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                    <span class="group-hover:text-primary transition-colors">{{ tls.description }}</span>
                  </label>
                </div>
              </div>

              <!-- Liaisons liste (checkboxes par type) -->
              <div v-for="(llt, i) in form.liaison_liste_textes_attributes" :key="`llt-${i}`" class="space-y-3">
                <p :class="labelCls">{{ llt.description }}</p>
                <input type="hidden" :name="`rapport[liaison_liste_textes_attributes][${i}][type_liaison_id]`" :value="llt.type_liaison_id" />
                <input type="hidden" v-if="llt.texte_id" :name="`rapport[liaison_liste_textes_attributes][${i}][id]`" :value="llt.texte_id" />
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <label v-for="item in llt.items" :key="item.id"
                         class="flex items-center gap-2 text-sm text-on-surface cursor-pointer group">
                    <input type="checkbox" :value="item.id" v-model="llt.selected_ids"
                           :name="`rapport[type_liaison_liste_ids][]`"
                           class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                    <span class="group-hover:text-primary transition-colors">{{ item.element }}</span>
                  </label>
                </div>
                <div class="flex items-center gap-3">
                  <span class="text-xs text-outline shrink-0">Autres :</span>
                  <input v-model="llt.texte_element" type="text"
                         :name="`rapport[liaison_liste_textes_attributes][${i}][element]`"
                         :class="inputSmCls + ' flex-1'" />
                </div>
              </div>

              <!-- Liaisons texte libre -->
              <div v-for="(lias, i) in form.liaisons_attributes" :key="`lias-${i}`" class="space-y-2">
                <label :class="labelCls">{{ lias.description }}</label>
                <input type="hidden" :name="`rapport[liaisons_attributes][${i}][type_liaison_id]`" :value="lias.type_liaison_id" />
                <input type="hidden" v-if="lias.id" :name="`rapport[liaisons_attributes][${i}][id]`" :value="lias.id" />
                <textarea v-model="lias.contenu" rows="3" :class="inputCls + ' resize-y'"></textarea>
              </div>

            </div>
          </div>

          <!-- ══════════════════════════════════════════
               8. COMMENTAIRES
               ══════════════════════════════════════════ -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Commentaires</h2>
            </div>
            <div class="p-6">
              <textarea v-model="form.commentaires" rows="6" :class="inputCls + ' resize-y'"></textarea>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               9. PHOTOS
               ══════════════════════════════════════════ -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHdrCls">
              <h2 :class="sectionTitleCls">Photos</h2>
            </div>
            <div class="p-6 space-y-3">
              <p class="text-sm text-outline">Ajoutez des photos en les sélectionnant.</p>
              <label class="inline-flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-outline hover:text-primary border border-outline-variant/20 rounded-md transition-all cursor-pointer">
                <span class="material-symbols-outlined text-base">folder_open</span>
                Parcourir les fichiers
                <input type="file" name="rapport[images][]" multiple accept="image/*" class="sr-only" @change="onImageChange" />
              </label>

              <!-- Aperçu des nouvelles photos -->
              <div v-if="newImagePreviews.length"
                   class="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                <div v-for="(img, i) in newImagePreviews" :key="`new-${i}`"
                     class="relative group rounded-lg overflow-hidden border border-outline-variant/20">
                  <img :src="img.src" :alt="img.name" class="w-full aspect-square object-cover" />
                  <div class="absolute top-1 left-1 bg-tertiary/80 text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded">
                    Nouveau
                  </div>
                  <button type="button" @click="removeNewImage(i)"
                          class="absolute top-1 right-1 p-1 rounded-md bg-error/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Retirer cette photo">
                    <span class="material-symbols-outlined text-sm">close</span>
                  </button>
                  <p class="text-[9px] text-outline mt-1 truncate px-1">{{ img.name }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════
               ACTIONS
               ══════════════════════════════════════════ -->
          <div class="flex justify-end gap-3 pb-6">
            <a :href="rapports_url"
               class="px-6 py-3 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all">
              Annuler
            </a>
            <button type="submit" :disabled="form.processing"
                    class="machined-sheen px-8 py-3 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-60">
              <span v-if="form.processing" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              <span v-else class="material-symbols-outlined text-sm">save</span>
              Enregistrer le bordereau
            </button>
          </div>

        </form>

        <footer class="pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>
      </div>
    </div>
  </AppLayout>
</template>
